/**
 * CompositionPanel — the four-case composition playground.
 *
 * Mirrors chapter 5 §5.0 / §5.3 of the manuscript: where multi-stage
 * AI pipelines live and where cross-SDP attacks emerge. The reader
 * picks two single-phase grades and watches `composeGraded` decide
 * Case A (replay), Case B (ICG envelope), Case C (cross-SDP), or
 * Case D (vacuous), with live ICG detection.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useMemo, useState } from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import {
  detectIcgEnvelope,
  formatGrade,
  neutralGrade,
  type Phase,
  type SdpCode,
} from "../algorithms/grade.js";
import { GradeVector } from "../components/GradeVector.js";
import { BridgePicker } from "./composition-panel/BridgePicker.js";
import {
  buildBridge,
  composeGraded,
  FOUR_CASES,
  PRESETS,
  summarize,
  type BridgeChoice,
} from "./composition-panel/model.js";

export function CompositionPanel(): React.JSX.Element {
  const [b1Choice, setB1Choice] = useState<BridgeChoice>({
    sdp: "Dt",
    phase: "eta",
  });
  const [b2Choice, setB2Choice] = useState<BridgeChoice>({
    sdp: "Pa",
    phase: "epsilon",
  });

  const b1 = useMemo(() => buildBridge(b1Choice), [b1Choice]);
  const b2 = useMemo(() => buildBridge(b2Choice), [b2Choice]);

  const verdict = useMemo(() => composeGraded(b1, b2), [b1, b2]);
  const summary = useMemo(() => summarize(verdict), [verdict]);

  const compositeGrade = useMemo(
    () =>
      verdict.outcome === "COMPOSED" ? verdict.composite.grade : neutralGrade(),
    [verdict],
  );

  const icgFromComposite = useMemo(
    () => detectIcgEnvelope(compositeGrade),
    [compositeGrade],
  );

  const applyPreset = useCallback((presetId: string) => {
    const preset = PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    if (preset.b1) setB1Choice(preset.b1);
    if (preset.b2) setB2Choice(preset.b2);
  }, []);

  const setB1Sdp = useCallback((sdp: SdpCode) => {
    setB1Choice((current) => ({ ...current, sdp }));
  }, []);
  const setB1Phase = useCallback((phase: Phase) => {
    setB1Choice((current) => ({ ...current, phase }));
  }, []);
  const setB2Sdp = useCallback((sdp: SdpCode) => {
    setB2Choice((current) => ({ ...current, sdp }));
  }, []);
  const setB2Phase = useCallback((phase: Phase) => {
    setB2Choice((current) => ({ ...current, phase }));
  }, []);

  return (
    <PanelSurface.PanelShell
      title="Composition · ∘"
      subtitle="CASE A · B · C · D · ICG DETECTION · WHERE MULTI-STAGE PIPELINES LIVE"
      accentColor="#818cf8"
    >
      <PanelSurface.Card glowColor="#818cf844">
        <PanelSurface.SectionHeading>Where multi-stage AI pipelines live</PanelSurface.SectionHeading>
        <p className="mt-2.5 text-[14px] leading-[1.65]">
          Real AI deployments are <strong>chains</strong>. A Planner agent hands
          a delegation to a Coder, which hands a build artifact to a Deployer; a
          retrieval driver hands a lineage-stamped chunk to a summarizer; an MCP
          router forwards a read capability from one server to another. Every
          chain is composition in the two-categorical sense — and composition is
          where cross-SDP confused deputies actually emerge.
        </p>
        <p className="mt-2.5 text-[13.5px] leading-[1.65] text-[var(--tcc-muted)]">
          The composite of two graded bridges carries the additive grade{" "}
          <PanelSurface.InlineCode>g₁ +_G g₂</PanelSurface.InlineCode>. Chapter 5 §5.3 sorts the result
          into four cases: <strong>A</strong> is replay (same grade twice; ℤ/2
          self-cancels to <PanelSurface.InlineCode>0_G</PanelSurface.InlineCode> and T1 refuses);{" "}
          <strong>B</strong> is the ICG envelope (same SDP, opposite phases —
          the both-phases-discharged certificate); <strong>C</strong> is
          cross-SDP (distinct SDPs; admissible only at a cross-SDP seam);{" "}
          <strong>D</strong> is vacuous (one input sits at{" "}
          <PanelSurface.InlineCode>0_G</PanelSurface.InlineCode>, which T1 rules out as a typable bridge
          in the first place).
        </p>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Presets</PanelSurface.SectionHeading>
        <p className="mt-2 mb-3 text-[13px] leading-[1.55] text-[var(--tcc-muted)]">
          Load an illustrative composition. Use the pickers below to vary it.
        </p>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              aria-label={`Preset ${preset.label}`}
              title={preset.note}
              onClick={() => {
                applyPreset(preset.id);
              }}
              className="cursor-pointer rounded-full border border-solid px-3 py-2 font-mono text-[12.5px] font-semibold"
              style={{
                borderColor: `${preset.accent}66`,
                background: `${preset.accent}14`,
                color: preset.accent,
              }}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </PanelSurface.Card>

      <div className="grid grid-cols-[minmax(0,1fr)_48px_minmax(0,1fr)] items-stretch gap-[18px]">
        <BridgePicker
          title="Bridge 1 · g₁"
          accent="#22d3ee"
          choice={b1Choice}
          onSdp={setB1Sdp}
          onPhase={setB1Phase}
        />
        <div
          aria-hidden
          className="flex items-center justify-center font-mono text-[var(--tcc-muted)]"
        >
          <div
            className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[var(--tcc-border)] text-[22px] font-extrabold text-[#818cf8]"
            style={{
              background:
                "radial-gradient(circle, rgba(129,140,248,0.18) 0%, transparent 70%)",
            }}
          >
            ∘
          </div>
        </div>
        <BridgePicker
          title="Bridge 2 · g₂"
          accent="#ec4899"
          choice={b2Choice}
          onSdp={setB2Sdp}
          onPhase={setB2Phase}
        />
      </div>

      <PanelSurface.Card
        glowColor={
          summary.outcome === "COMPOSED" ? `${summary.accent}55` : "#ef444455"
        }
      >
        <PanelSurface.SectionHeading>Result · composeGraded(b₁, b₂)</PanelSurface.SectionHeading>
        <div className="mt-3">
          <PanelSurface.VerdictBanner
            outcome={summary.outcome === "COMPOSED" ? "ADMITTED" : "REFUSED"}
            tag={summary.tag}
            explanation={summary.explanation}
          />
        </div>

        <div
          className="mt-4 rounded-xl border border-[var(--tcc-border)] px-4 py-3.5"
          style={{
            background:
              "linear-gradient(180deg, rgba(129,140,248,0.06), rgba(5,8,16,0.5))",
          }}
        >
          <div className="mb-2 font-mono text-[11px] tracking-[0.06em] text-[var(--tcc-muted)]">
            COMPOSITE GRADE · g₁ +_G g₂
          </div>
          <GradeVector grade={compositeGrade} label="Composite grade" />
        </div>

        <div className="mt-4 flex flex-wrap gap-3">
          <PanelSurface.KpiBox
            label="Bridge 1 grade"
            value={formatGrade(b1.grade)}
            color="#22d3ee"
          />
          <PanelSurface.KpiBox
            label="Bridge 2 grade"
            value={formatGrade(b2.grade)}
            color="#ec4899"
          />
          <PanelSurface.KpiBox
            label="Composite grade"
            value={formatGrade(compositeGrade)}
            color={summary.accent}
          />
          <PanelSurface.KpiBox
            label="Case"
            value={summary.caseId}
            color={summary.accent}
            subtitle={summary.tag}
          />
          <PanelSurface.KpiBox
            label="ICG?"
            value={
              summary.caseId === "B"
                ? summary.icg
                : icgFromComposite
                  ? icgFromComposite.icg
                  : "—"
            }
            color={
              summary.caseId === "B" || icgFromComposite ? "#a78bfa" : panelTokens.muted
            }
          />
        </div>

        <PanelSurface.Mono style={{ marginTop: 16 }}>
          {[
            "-- chapter 5 §5.3 — four-case composition verdict",
            `b₁ : GradedBridge A B  at grade  ${formatGrade(b1.grade)}`,
            `b₂ : GradedBridge B C  at grade  ${formatGrade(b2.grade)}`,
            "",
            `composeGraded b₁ b₂ : CompositionVerdict`,
            `   outcome   = ${summary.outcome}`,
            `   case      = ${summary.caseId}`,
            `   composite = ${formatGrade(compositeGrade)}`,
            `   icg       = ${summary.icg}`,
            "",
            `-- ${summary.explanation}`,
          ].join("\n")}
        </PanelSurface.Mono>

        <div className="mt-3 flex flex-wrap gap-2">
          {summary.caseId === "A" ? PanelSurface.badge("refused by T1", panelTokens.accentRed) : null}
          {summary.caseId === "D"
            ? PanelSurface.badge("refused by T1 / input uninhabited", panelTokens.accentRed)
            : null}
          {summary.caseId === "B"
            ? PanelSurface.badge(`composes into ${summary.icg}`, "#a78bfa")
            : null}
          {summary.caseId === "C"
            ? PanelSurface.badge("admissible at cross-SDP seam only", "#22d3ee")
            : null}
          {PanelSurface.badge(`T3 · composite = g₁ +_G g₂`, "#a78bfa")}
        </div>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>The four cases (chapter 5 §5.3)</PanelSurface.SectionHeading>
        <div className="mt-3 grid grid-cols-[minmax(0,1fr)] gap-2.5">
          {FOUR_CASES.map((row) => {
            const active = row.id === summary.caseId;
            return (
              <div
                key={row.id}
                className="grid grid-cols-[44px_minmax(0,1fr)_minmax(0,1.4fr)] items-center gap-3.5 rounded-xl border border-solid px-3.5 py-3"
                style={{
                  borderColor: active ? row.accent : panelTokens.border,
                  background: active
                    ? `linear-gradient(135deg, ${row.accent}1a, transparent 80%)`
                    : "rgba(5,8,16,0.45)",
                  boxShadow: active ? `0 0 16px ${row.accent}33` : "none",
                  transition: "all 140ms ease",
                }}
              >
                <div
                  aria-hidden
                  className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-solid font-mono text-[16px] font-extrabold"
                  style={{
                    borderColor: `${row.accent}66`,
                    background: `${row.accent}14`,
                    color: row.accent,
                  }}
                >
                  {row.id}
                </div>
                <div>
                  <div className="text-[13.5px] font-bold text-[var(--tcc-bright)]">
                    {row.title}
                  </div>
                  <div className="mt-0.5 font-mono text-[12px] text-[var(--tcc-muted)]">
                    {row.trigger}
                  </div>
                </div>
                <div className="text-[13px] leading-[1.5] text-[var(--tcc-text)]">
                  {row.outcome}
                </div>
              </div>
            );
          })}
        </div>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Security pay-off</PanelSurface.SectionHeading>
        <ul className="mt-2.5 ml-[18px] p-0 text-[13.5px] leading-[1.7]">
          <li>
            <strong>T3 (composition-ICG):</strong> the composite grade is
            uniquely determined by <PanelSurface.InlineCode>g₁ +_G g₂</PanelSurface.InlineCode>. There is
            no <em>pick-your-composite</em> attack — the operation yields
            exactly one grade and the matching ICG (when same SDP) is a
            consequence, not a choice.
          </li>
          <li>
            <strong>Orchestrator confused deputies refused:</strong> a multi-hop
            orchestrator cannot smuggle a Tr-graded bridge into a position the
            chain treats as Dt-graded. Case C forces the cross-SDP seam to
            exist; absent that seam the chain refuses.
          </li>
          <li>
            <strong>Replay refused by Case A:</strong> presenting the same grant
            twice (autonomous CI/CD replaying a precommit; an agent replaying
            yesterday's delegation) self-cancels to <PanelSurface.InlineCode>0_G</PanelSurface.InlineCode>
            . T1 then refuses by uninhabited grade — no TTL, no centralized
            revocation, no runtime check.
          </li>
        </ul>
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}
